import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Trash2, ShoppingCart, Loader2, Save, Send, X, User } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";

export const OrderBuilder = ({ user, onDone }) => {
  const role = user?.role;
  const isAgent = role === 'tc_admin' || role === 'sales_rep';
  const isAdmin = role === 'tc_admin';
  const canPrice = role === 'tc_admin' || role === 'sales_rep'; // who may set special order pricing
  const [clients, setClients] = useState([]);
  const [customerId, setCustomerId] = useState(isAgent ? '' : user?.user_id);
  const [clientQuery, setClientQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [prodQuery, setProdQuery] = useState('');
  const [lines, setLines] = useState([]); // {product_id, name, price, qty, stock}
  const [customItems, setCustomItems] = useState([]);
  const [newCustom, setNewCustom] = useState({ name: '', price: '', quantity: 1 });
  const [shipping, setShipping] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingProd, setLoadingProd] = useState(false);
  const [discUI, setDiscUI] = useState({}); // pid -> { val, mode: '%'|'$' } (admin special pricing helper)
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  // load clients for agents
  useEffect(() => {
    if (!isAgent) return;
    (async () => {
      try {
        if (role === 'sales_rep') {
          const r = await axios.get(`${API}/api/sales-rep/clients`);
          setClients((r.data.clients || []).map((c) => ({ id: c.user_id, label: c.company_name || c.full_name })));
        } else {
          const r = await axios.get(`${API}/api/users?limit=500`);
          setClients((r.data.users || []).filter((u) => ['installation_center', 'elite_partner'].includes(u.role))
            .map((c) => ({ id: c.user_id, label: c.company_name || c.full_name, source: c.account_management_type })));
        }
      } catch (e) { /* ignore */ }
    })();
  }, [isAgent, role]);

  // load products priced for the selected customer
  const loadProducts = async (cid) => {
    setLoadingProd(true);
    try {
      const url = cid && isAgent ? `${API}/api/products?limit=300&customer_id=${cid}` : `${API}/api/products?limit=300`;
      const r = await axios.get(url);
      setProducts(r.data.products || []);
    } catch (e) { toast.error('Could not load products.'); }
    finally { setLoadingProd(false); }
  };
  useEffect(() => { if (!isAgent || customerId) loadProducts(customerId); }, [customerId]); // eslint-disable-line

  const addLine = (p) => {
    const ref = round2(p.discounted_price ?? p.price);
    setLines((ls) => ls.find((l) => l.product_id === p.product_id)
      ? ls.map((l) => l.product_id === p.product_id ? { ...l, qty: l.qty + 1 } : l)
      : [...ls, { product_id: p.product_id, name: p.name, refPrice: ref, finalPrice: ref, sku: p.sku || null, qty: 1 }]);
  };
  const setQty = (pid, qty) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, qty: Math.max(1, qty) } : l));
  const removeLine = (pid) => setLines((ls) => ls.filter((l) => l.product_id !== pid));

  // Admin special pricing: direct final-price edit OR discount (% / $). Either method, never conflicting.
  const setFinalPrice = (pid, val) => {
    setDiscUI((d) => ({ ...d, [pid]: { ...(d[pid] || { mode: '%' }), val: '' } }));
    setLines((ls) => ls.map((l) => {
      if (l.product_id !== pid) return l;
      if (val === '' || val == null) return { ...l, finalPrice: l.refPrice };
      const v = parseFloat(val);
      if (isNaN(v) || v < 0) return l;
      return { ...l, finalPrice: round2(v) };
    }));
  };
  const applyDiscount = (pid, raw, mode) => {
    setLines((ls) => ls.map((l) => {
      if (l.product_id !== pid) return l;
      const num = parseFloat(raw);
      let err = '';
      if (mode === '%' && num > 100) err = 'Discount cannot exceed 100%';
      else if (mode === '$' && num > l.refPrice) err = 'Discount cannot exceed the unit price';
      setDiscUI((d) => ({ ...d, [pid]: { val: raw, mode, err } }));
      if (raw === '' || isNaN(num) || num < 0) return { ...l, finalPrice: l.refPrice };
      let fp = l.refPrice;
      if (mode === '%') fp = l.refPrice * (1 - Math.min(100, num) / 100);
      else fp = l.refPrice - Math.min(num, l.refPrice);
      return { ...l, finalPrice: round2(Math.max(0, fp)) };
    }));
  };
  const clearOverride = (pid) => {
    setDiscUI((d) => ({ ...d, [pid]: { val: '', mode: (d[pid]?.mode) || '%' } }));
    setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, finalPrice: l.refPrice } : l));
  };
  const lineFinal = (l) => (typeof l.finalPrice === 'number' ? l.finalPrice : l.refPrice);
  const isOverridden = (l) => Math.abs(lineFinal(l) - l.refPrice) > 0.004;

  const addCustom = () => {
    if (!newCustom.name || !newCustom.price) { toast.error('Custom item needs a name and price.'); return; }
    setCustomItems((c) => [...c, { ...newCustom, price: parseFloat(newCustom.price), quantity: parseInt(newCustom.quantity) || 1 }]);
    setNewCustom({ name: '', price: '', quantity: 1 });
  };

  const regularSubtotal = lines.reduce((s, l) => s + l.refPrice * l.qty, 0);
  const finalLinesSubtotal = lines.reduce((s, l) => s + lineFinal(l) * l.qty, 0);
  const specialDiscounts = round2(regularSubtotal - finalLinesSubtotal);
  const customTotal = customItems.reduce((s, c) => s + c.price * c.quantity, 0);
  const total = finalLinesSubtotal + customTotal;

  const filteredProducts = useMemo(() => {
    const s = prodQuery.trim().toLowerCase();
    return s ? products.filter((p) => (p.name || '').toLowerCase().includes(s) || (p.category || '').toLowerCase().includes(s)) : products;
  }, [products, prodQuery]);
  const filteredClients = useMemo(() => {
    const s = clientQuery.trim().toLowerCase();
    return s ? clients.filter((c) => c.label.toLowerCase().includes(s)) : clients;
  }, [clients, clientQuery]);

  const submit = async (asDraft) => {
    if (isAgent && !customerId) { toast.error('Select a customer first.'); return; }
    if (lines.length === 0 && customItems.length === 0) { toast.error('Add at least one item.'); return; }
    if (!shipping.trim()) { toast.error('Enter a shipping address.'); return; }
    setSaving(true);
    try {
      const payload = {
        items: lines.map((l) => ({
          product_id: l.product_id,
          quantity: l.qty,
          ...(canPrice && isOverridden(l) ? { custom_price: round2(lineFinal(l)) } : {}),
        })),
        custom_items: customItems,
        shipping_address: shipping,
        special_instructions: notes,
        is_draft: asDraft,
      };
      if (isAgent) payload.customer_id = customerId;
      const r = await axios.post(`${API}/api/orders`, payload);
      toast.success(asDraft ? `Draft ${r.data.order_number} saved` : `Order ${r.data.order_number} submitted`);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create order.'); }
    finally { setSaving(false); }
  };

  const selectedClient = clients.find((c) => c.id === customerId);

  return (
    <div className="space-y-5" data-testid="order-builder">
      <h2 className="text-2xl font-semibold text-white">Create Order</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main */}
        <div className="lg:col-span-2 space-y-5">
          {/* Customer */}
          {isAgent && (
            <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> Customer</h3>
              {selectedClient ? (
                <div className="flex items-center justify-between bg-black/30 rounded-lg px-4 py-3">
                  <div><p className="text-slate-100 text-sm">{selectedClient.label}</p>
                    {selectedClient.source && <p className="text-slate-500 text-xs">Managed by: {selectedClient.source === 'SALES_REP' ? 'Sales Rep' : 'TC Direct'}</p>}</div>
                  <button onClick={() => { setCustomerId(''); setLines([]); }} className="text-slate-400 hover:text-white" data-testid="clear-customer"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input className={`${inputCls} pl-9`} placeholder="Search your clients…" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} data-testid="builder-client-search" />
                  </div>
                  <div className="max-h-44 overflow-y-auto space-y-1">
                    {filteredClients.length === 0 ? <p className="text-slate-500 text-sm py-3 text-center">No clients.</p> :
                      filteredClients.map((c) => (
                        <button key={c.id} onClick={() => setCustomerId(c.id)} data-testid={`builder-client-${c.id}`}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/[0.04] text-sm text-slate-200">{c.label}</button>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Products */}
          <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-slate-400" /> Products</h3>
            {isAgent && !customerId ? <p className="text-slate-500 text-sm">Select a customer to see authorized pricing.</p> : (
              <>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input className={`${inputCls} pl-9`} placeholder="Search products…" value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} data-testid="builder-product-search" />
                </div>
                {loadingProd ? <p className="text-slate-500 text-sm">Loading…</p> : (
                  <div className="max-h-56 overflow-y-auto space-y-1">
                    {filteredProducts.map((p) => {
                      const isAdmin = role === 'tc_admin';
                      const out = isAdmin ? (p.stock_quantity || 0) <= 0 : p.availability === 'out_of_stock';
                      const low = isAdmin && !out && p.stock_quantity <= 15;
                      return (
                        <div key={p.product_id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/[0.03]" data-testid={`builder-product-${p.product_id}`}>
                          <div className="min-w-0">
                            <p className="text-slate-100 text-sm truncate">{p.name}</p>
                            <p className="text-xs"><span className="text-[#C6A15B]">{money(p.discounted_price ?? p.price)}</span>
                              <span className={`ml-2 ${out ? 'text-red-400' : low ? 'text-amber-400' : 'text-slate-500'}`}>{out ? 'Out of Stock' : (isAdmin ? `${p.stock_quantity} available${low ? ' · Low' : ''}` : 'Available')}</span></p>
                          </div>
                          <button disabled={out} onClick={() => addLine(p)} data-testid={`builder-add-${p.product_id}`}
                            className="shrink-0 inline-flex items-center gap-1 bg-white/[0.06] hover:bg-[#C6A15B] hover:text-black text-slate-200 text-xs px-2.5 py-1.5 rounded-lg disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Add</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Custom items (admin + sales_rep) */}
          {isAgent && (
            <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-1">Custom Items</h3>
              {role === 'sales_rep' && <p className="text-[11px] text-amber-300/80 mb-3">Custom items are flagged for TC review.</p>}
              <div className="flex flex-col sm:flex-row gap-2">
                <input className={inputCls} placeholder="Description" value={newCustom.name} onChange={(e) => setNewCustom({ ...newCustom, name: e.target.value })} data-testid="custom-name" />
                <input className={`${inputCls} sm:w-28`} type="number" placeholder="Price" value={newCustom.price} onChange={(e) => setNewCustom({ ...newCustom, price: e.target.value })} data-testid="custom-price" />
                <input className={`${inputCls} sm:w-20`} type="number" placeholder="Qty" value={newCustom.quantity} onChange={(e) => setNewCustom({ ...newCustom, quantity: e.target.value })} data-testid="custom-qty" />
                <button onClick={addCustom} className="bg-white/[0.06] hover:bg-white/[0.12] text-slate-200 text-sm px-4 rounded-lg py-2.5" data-testid="custom-add">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-[#14171B] border border-white/[0.06] rounded-2xl p-5 lg:sticky lg:top-20 self-start">
          <h3 className="text-white font-semibold mb-4">Order Summary</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto mb-3">
            {lines.length === 0 && customItems.length === 0 && <p className="text-slate-500 text-sm">No items yet.</p>}
            {lines.map((l) => {
              const dui = discUI[l.product_id] || { val: '', mode: '%' };
              const over = isOverridden(l);
              const eqPct = l.refPrice > 0 ? round2(((l.refPrice - lineFinal(l)) / l.refPrice) * 100) : 0;
              const eqAmt = round2(l.refPrice - lineFinal(l));
              return (
                <div key={l.product_id} className="text-sm border-b border-white/[0.04] pb-2 mb-2 last:border-0" data-testid={`summary-line-${l.product_id}`}>
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-200 truncate">{l.name}{l.sku ? <span className="text-slate-600 text-[11px]"> · {l.sku}</span> : null}</p>
                      <p className="text-slate-500 text-xs">
                        {over ? <span className="line-through text-slate-600 mr-1">{money(l.refPrice)}</span> : null}
                        <span className={over ? 'text-[#C6A15B]' : ''}>{money(lineFinal(l))}</span> each
                      </p>
                    </div>
                    <input type="number" min="1" value={l.qty} onChange={(e) => setQty(l.product_id, parseInt(e.target.value) || 1)}
                      className="w-14 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-slate-100" data-testid={`summary-qty-${l.product_id}`} />
                    <span className="w-16 text-right text-white" data-testid={`summary-line-total-${l.product_id}`}>{money(lineFinal(l) * l.qty)}</span>
                    <button onClick={() => removeLine(l.product_id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {canPrice && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap bg-black/20 rounded-lg px-2 py-1.5">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">Discount</span>
                      <input type="number" min="0" placeholder="0" value={dui.val}
                        onChange={(e) => applyDiscount(l.product_id, e.target.value, dui.mode)}
                        className="w-14 bg-black/40 border border-white/10 rounded px-1.5 py-1 text-xs text-slate-100" data-testid={`disc-input-${l.product_id}`} />
                      <div className="flex rounded-md overflow-hidden border border-white/10">
                        {['%', '$'].map((m) => (
                          <button key={m} onClick={() => applyDiscount(l.product_id, dui.val, m)}
                            className={`px-2 py-1 text-xs ${dui.mode === m ? 'bg-[#C6A15B] text-black font-medium' : 'text-slate-400'}`}
                            data-testid={`disc-mode-${m === '%' ? 'pct' : 'amt'}-${l.product_id}`}>{m}</button>
                        ))}
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-500 ml-1">Final</span>
                      <span className="text-slate-500 text-xs">$</span>
                      <input type="number" min="0" step="0.01" value={over || dui.val ? lineFinal(l) : ''} placeholder={l.refPrice.toFixed(2)}
                        onChange={(e) => setFinalPrice(l.product_id, e.target.value)}
                        className="w-20 bg-black/40 border border-white/10 rounded px-1.5 py-1 text-xs text-slate-100" data-testid={`final-price-${l.product_id}`} />
                      {over && (
                        <>
                          <span className="text-[11px] text-emerald-400" data-testid={`disc-eq-${l.product_id}`}>-{eqPct}% / {money(eqAmt)}</span>
                          <button onClick={() => clearOverride(l.product_id)} className="text-[11px] text-slate-400 hover:text-white underline" data-testid={`clear-override-${l.product_id}`}>reset</button>
                        </>
                      )}
                      {dui.err && <span className="text-[11px] text-red-400 w-full" data-testid={`disc-err-${l.product_id}`}>{dui.err} — capped at $0.00</span>}
                    </div>
                  )}
                </div>
              );
            })}
            {customItems.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="min-w-0 flex-1"><p className="text-slate-200 truncate">{c.name} <span className="text-[10px] text-amber-300">custom</span></p><p className="text-slate-500 text-xs">{c.quantity} × {money(c.price)}</p></div>
                <span className="w-16 text-right text-white">{money(c.price * c.quantity)}</span>
                <button onClick={() => setCustomItems((cs) => cs.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="border-t border-white/[0.06] pt-3 space-y-1 text-sm">
            {canPrice && specialDiscounts > 0 ? (
              <>
                <div className="flex justify-between text-slate-400"><span>Regular Subtotal</span><span data-testid="summary-regular-subtotal">{money(regularSubtotal)}</span></div>
                <div className="flex justify-between text-emerald-400"><span>Special Discounts</span><span data-testid="summary-special-discounts">-{money(specialDiscounts)}</span></div>
                <div className="flex justify-between text-slate-300"><span>Final Subtotal</span><span data-testid="summary-final-subtotal">{money(finalLinesSubtotal)}</span></div>
              </>
            ) : (
              <div className="flex justify-between text-slate-400"><span>Subtotal</span><span data-testid="summary-subtotal">{money(finalLinesSubtotal)}</span></div>
            )}
            {customTotal > 0 && <div className="flex justify-between text-slate-400"><span>Custom Items</span><span>{money(customTotal)}</span></div>}
            <div className="flex justify-between text-white font-semibold text-base pt-1"><span>Order Total</span><span data-testid="summary-total">{money(total)}</span></div>
          </div>
          <div className="mt-4 space-y-2">
            <input className={inputCls} placeholder="Shipping address" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="builder-shipping" />
            <textarea className={inputCls} rows="2" placeholder="Order notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="builder-notes" />
          </div>
          <div className="mt-4 space-y-2">
            {role === 'sales_rep' && (
              <button disabled={saving} onClick={() => submit(true)} data-testid="save-draft-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-slate-200 font-medium text-sm px-4 py-2.5 rounded-xl disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save as Draft
              </button>
            )}
            <button disabled={saving} onClick={() => submit(false)} data-testid="submit-order-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {role === 'sales_rep' ? 'Submit to TC' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderBuilder;
